import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import socketService from '../services/socket';
import './DocumentRequest.css';

// ข้อมูลอธิบายความแตกต่างของเอกสารแต่ละประเภท — ผู้ใช้มักสับสนว่า 50 ทวิ / หนังสือรับรองเงินเดือน
// คืออันเดียวกับสลิปเงินเดือน ทั้งที่การกู้เงิน (โดยเฉพาะสหกรณ์ออมทรัพย์ รพ.) ใช้ "สลิปเงินเดือน" เป็นหลัก
const DOC_INFO = {
    salary_slip: {
        name: 'สลิปเงินเดือน',
        use: 'กู้เงิน / ขอสินเชื่อ / แสดงรายได้รายเดือน',
        desc: 'แสดงรายรับ–รายการหักของแต่ละเดือน สหกรณ์ออมทรัพย์และธนาคารใช้เอกสารนี้เป็นหลักฐานรายได้ประกอบการกู้ยืมเงิน'
    },
    tax_cert: {
        name: 'หนังสือรับรองภาษี (50 ทวิ)',
        use: 'ยื่นภาษีเงินได้ประจำปี (ภ.ง.ด.90/91) เท่านั้น',
        desc: 'สรุปรายได้ทั้งปีและภาษีที่ถูกหัก ณ ที่จ่าย ใช้สำหรับยื่นภาษี/ขอคืนภาษี ไม่ใช่เอกสารสำหรับกู้เงิน'
    },
    salary_cert: {
        name: 'หนังสือรับรองเงินเดือน',
        use: 'หน่วยงานภายนอกระบุว่าต้องมี "หนังสือรับรอง" ลงนามโดยหน่วยงาน',
        desc: 'หนังสือราชการลงนามรับรองโดยผู้มีอำนาจ เช่น ใช้ยื่นวีซ่า ค้ำประกัน สมัครงาน — จัดทำโดยฝ่ายการเงิน ใช้เวลารอนานกว่า'
    }
};

// วัตถุประสงค์การใช้เอกสาร → เอกสารที่ควรใช้ (recommend)
const PURPOSES = [
    { value: 'loan', label: 'กู้เงิน / ขอสินเชื่อ (สหกรณ์ออมทรัพย์, ธนาคาร)', short: 'ใช้กู้เงิน/ขอสินเชื่อ', recommend: 'salary_slip' },
    { value: 'tax', label: 'ยื่นภาษีเงินได้ประจำปี / ขอคืนภาษี', short: 'ใช้ยื่นภาษีประจำปี', recommend: 'tax_cert' },
    { value: 'external', label: 'หน่วยงานภายนอกขอ "หนังสือรับรอง" ที่มีลายเซ็นหน่วยงาน (วีซ่า, ค้ำประกัน ฯลฯ)', short: 'ยื่นหน่วยงานภายนอกที่ต้องการหนังสือรับรอง', recommend: 'salary_cert' },
    { value: 'other', label: 'อื่น ๆ / ไม่แน่ใจ', short: '', recommend: null }
];

const MODE_KEY_BY_TYPE = {
    salary_slip: 'slip_request_mode',
    tax_cert: 'tax_cert_request_mode',
    salary_cert: 'salary_cert_request_mode'
};

function DocumentRequest() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const emailVerified = !!user?.email_verified;
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [requestModes, setRequestModes] = useState({
        slip_request_mode: false,
        tax_cert_request_mode: false,
        salary_cert_request_mode: false
    }); // แยก mode ตามประเภทเอกสาร
    const [formData, setFormData] = useState({
        request_type: '', // จะกำหนดตามประเภทที่ "เปิดรับคำร้อง" หลังโหลด requestModes
        document_year: new Date().getFullYear(),
        document_month: new Date().getMonth() + 1,
        contact_phone: '',
        request_reason: '',
        // เฉพาะหนังสือรับรองเงินเดือน — นัดวันเวลาเข้ามาเขียนคำร้องที่ห้องการเงิน
        appointment_date: '',
        appointment_period: 'morning'
    });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [notification, setNotification] = useState(null);

    // วัตถุประสงค์การใช้เอกสาร — ให้ผู้ใช้ระบุก่อน เพื่อแนะนำเอกสารที่ถูกต้อง (ลดการขอเอกสารผิดประเภท)
    const [purpose, setPurpose] = useState('');
    // ติ๊กยืนยันเมื่อเลือกเอกสารไม่ตรงกับที่ระบบแนะนำตามวัตถุประสงค์
    const [mismatchConfirmed, setMismatchConfirmed] = useState(false);

    const purposeObj = PURPOSES.find(p => p.value === purpose) || null;
    const recommendedType = purposeObj?.recommend || null;
    // เอกสารที่แนะนำ "เปิดรับคำร้อง" อยู่หรือไม่ (ถ้าปิด = พิมพ์ได้เองจากหน้าข้อมูลเงินเดือน)
    const recommendedModeOn = recommendedType ? !!requestModes[MODE_KEY_BY_TYPE[recommendedType]] : false;
    // เลือกเอกสารไม่ตรงกับที่แนะนำ → ต้องยืนยันก่อนส่ง
    const typeMismatch = !!(recommendedType && formData.request_type && formData.request_type !== recommendedType);

    // ==== คำขอ "หนังสือรับรองเงินเดือน" = ระบบนัดหมาย ====
    // ผู้ขอต้องมาเขียนคำร้องด้วยตนเองที่ห้องการเงิน — การยื่นออนไลน์คือการนัดวันเวลาเท่านั้น
    // (ด่านติ๊ก 3 ข้อ + แนบรูปบัตรถูกปิดไว้ เปลี่ยนเป็นติ๊กรับทราบข้อเดียวว่าต้องมาเขียนคำร้องเอง)
    const [certAckInPerson, setCertAckInPerson] = useState(false);

    const handlePurposeChange = (value) => {
        setPurpose(value);
        setMismatchConfirmed(false);
        const rec = PURPOSES.find(p => p.value === value)?.recommend;
        // เลือกเอกสารที่แนะนำให้อัตโนมัติ ถ้าประเภทนั้นเปิดรับคำร้องอยู่
        if (rec && requestModes[MODE_KEY_BY_TYPE[rec]]) {
            setFormData(prev => ({
                ...prev,
                request_type: rec,
                // 50 ทวิ: รายปีเท่านั้น และปีล่าสุด = ปีที่แล้ว (ปีปัจจุบันยังไม่จบ ออกไม่ได้)
                document_year: rec === 'tax_cert'
                    ? Math.min(prev.document_year, new Date().getFullYear() - 1)
                    : prev.document_year,
                document_month: rec === 'salary_slip' ? (prev.document_month || new Date().getMonth() + 1) : null,
                request_reason: rec === 'salary_cert' ? prev.request_reason : ''
            }));
        }
    };

    // ติดตามคำร้อง (ส่งข้อความถึงเจ้าหน้าที่)
    const [followUpFor, setFollowUpFor] = useState(null); // request.id ที่กำลังเปิดฟอร์ม
    const [followUpText, setFollowUpText] = useState('');
    const [followUpSending, setFollowUpSending] = useState(false);

    const openFollowUp = (id) => {
        setFollowUpFor(id);
        setFollowUpText('');
        setMessage({ type: '', text: '' });
    };

    const sendFollowUp = async (id) => {
        if (!followUpText.trim()) {
            setMessage({ type: 'error', text: 'กรุณาพิมพ์ข้อความถึงเจ้าหน้าที่' });
            return;
        }
        setFollowUpSending(true);
        try {
            const res = await api.post(`/request/${id}/follow-up`, { message: followUpText.trim() });
            setMessage({ type: 'success', text: res.data.message || 'ส่งข้อความแล้ว' });
            setFollowUpFor(null);
            setFollowUpText('');
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || err.response?.data?.error || 'ส่งข้อความไม่สำเร็จ' });
        } finally {
            setFollowUpSending(false);
        }
    };

    const fetchRequests = useCallback(async () => {
        try {
            const response = await api.get('/request/my-requests');
            setRequests(response.data);
        } catch (err) {
            console.error('Error fetching requests:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Mark notifications as read when entering this page
    const markNotificationsAsRead = useCallback(async () => {
        try {
            await api.post('/request/mark-read');
        } catch (err) {
            // Ignore error
        }
    }, []);

    // Fetch request_mode settings (แยกตามประเภทเอกสาร)
    const fetchRequestModes = useCallback(async () => {
        try {
            const response = await api.get('/request-mode');
            const modes = {
                slip_request_mode: response.data.slip_request_mode ?? false,
                tax_cert_request_mode: response.data.tax_cert_request_mode ?? false,
                salary_cert_request_mode: response.data.salary_cert_request_mode ?? false
            };
            console.log('Request modes from API:', modes);
            setRequestModes(modes);

            // กำหนด request_type ให้เป็นประเภทแรกที่ "เปิดรับคำร้อง" เท่านั้น
            // ถ้าประเภทที่เลือกอยู่ถูกปิด จะสลับไปประเภทที่เปิดอยู่ หรือเว้นว่างถ้าไม่มีประเภทใดเปิด
            const enabledTypes = [];
            if (modes.slip_request_mode) enabledTypes.push('salary_slip');
            if (modes.tax_cert_request_mode) enabledTypes.push('tax_cert');
            if (modes.salary_cert_request_mode) enabledTypes.push('salary_cert');
            setFormData(prev => ({
                ...prev,
                request_type: enabledTypes.includes(prev.request_type) ? prev.request_type : (enabledTypes[0] || '')
            }));
        } catch (err) {
            console.error('Error fetching request modes:', err);
            // Default values if error — ปิดรับทุกประเภทไว้ก่อน (ปลอดภัยกว่า)
            setRequestModes({
                slip_request_mode: false,
                tax_cert_request_mode: false,
                salary_cert_request_mode: false
            });
        }
    }, []);

    useEffect(() => {
        fetchRequests();
        markNotificationsAsRead();
        fetchRequestModes();
    }, [fetchRequests, markNotificationsAsRead, fetchRequestModes]);

    // Listen for realtime updates via Socket.IO
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const socket = socketService.connect(token);
            console.log('DocumentRequest: Socket connecting...');

            const handleRequestUpdate = (data) => {
                console.log('DocumentRequest: Received request_updated event:', data);
                // Show notification
                setNotification({
                    type: data.type === 'approved' ? 'success' : 'error',
                    message: data.message
                });

                // Refresh requests list
                fetchRequests();

                // Auto-hide notification after 5 seconds
                setTimeout(() => {
                    setNotification(null);
                }, 5000);
            };

            socketService.on('request_updated', handleRequestUpdate);

            // Debug: Check socket connection status
            if (socket) {
                socket.on('connect', () => {
                    console.log('DocumentRequest: Socket connected!', socket.id);
                });
            }

            return () => {
                socketService.off('request_updated', handleRequestUpdate);
            };
        }
    }, [fetchRequests]);

    // ทุกประเภทเอกสารต้องยืนยันอีเมลก่อน (ดูข้อมูลได้ แต่ขอ/ออกเอกสารต้องยืนยันอีเมล)
    const needsVerifiedEmail = true;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // ไม่มีประเภทเอกสารที่ "เปิดรับคำร้อง" ในขณะนี้
        if (!formData.request_type) {
            setMessage({ type: 'error', text: 'ขณะนี้ไม่มีเอกสารที่ต้องยื่นคำร้อง' });
            return;
        }

        // บังคับระบุวัตถุประสงค์ก่อน — ใช้ตรวจว่าเลือกเอกสารถูกประเภทหรือไม่
        if (!purpose) {
            setMessage({ type: 'error', text: 'กรุณาเลือกวัตถุประสงค์การใช้เอกสารก่อน เพื่อให้ระบบตรวจสอบว่าท่านเลือกเอกสารถูกประเภท' });
            return;
        }

        // เลือกเอกสารไม่ตรงกับวัตถุประสงค์ → ต้องติ๊กยืนยันว่าเข้าใจความแตกต่างแล้ว
        if (typeMismatch && !mismatchConfirmed) {
            setMessage({ type: 'error', text: `เอกสารที่เลือกไม่ตรงกับวัตถุประสงค์ที่ระบุ (${DOC_INFO[recommendedType]?.name} คือเอกสารที่เหมาะสม) หากยืนยันว่าต้องการ${DOC_INFO[formData.request_type]?.name}จริง กรุณาติ๊กช่องยืนยันด้านล่างก่อนส่งคำร้อง` });
            return;
        }

        // บังคับยืนยันอีเมลสำหรับหนังสือรับรอง / 50 ทวิ
        if (needsVerifiedEmail && !emailVerified) {
            setMessage({ type: 'error', text: 'กรุณายืนยันอีเมลในหน้าโปรไฟล์ก่อนยื่นคำขอเอกสารนี้ เพราะระบบจะแจ้งทางอีเมลเมื่อเอกสารพร้อม' });
            return;
        }

        // บังคับกรอกเบอร์โทรติดต่อกลับ — เจ้าหน้าที่ใช้ติดต่อกลับ
        const phoneDigits = (formData.contact_phone || '').replace(/\D/g, '');
        if (!phoneDigits) {
            setMessage({ type: 'error', text: 'กรุณากรอกเบอร์โทรติดต่อกลับ เพื่อให้เจ้าหน้าที่ติดต่อกลับได้' });
            return;
        }
        if (!/^0\d{8,9}$/.test(phoneDigits)) {
            setMessage({ type: 'error', text: 'เบอร์โทรติดต่อกลับไม่ถูกต้อง (เบอร์มือถือ 10 หลัก หรือเบอร์บ้าน 9 หลัก)' });
            return;
        }

        // หนังสือรับรองเงินเดือน = ระบบนัดหมาย: ต้องเลือกวันเวลา + เหตุผล + ยืนยันว่าเข้าใจว่าต้องมาเขียนคำร้องเอง
        if (formData.request_type === 'salary_cert') {
            if (!(formData.request_reason || '').trim()) {
                setMessage({ type: 'error', text: 'กรุณาระบุเหตุผลในการขอ เช่น ใช้ยื่นขอสินเชื่อกับธนาคารใด สาขาใด' });
                return;
            }
            if (!formData.appointment_date) {
                setMessage({ type: 'error', text: 'กรุณาเลือกวันที่จะเข้ามาเขียนคำร้องที่ห้องการเงิน' });
                return;
            }
            const todayStr = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD ตามเวลาเครื่อง
            if (formData.appointment_date < todayStr) {
                setMessage({ type: 'error', text: 'วันนัดหมายต้องเป็นวันนี้หรือวันในอนาคต' });
                return;
            }
            if (!certAckInPerson) {
                setMessage({ type: 'error', text: 'กรุณาติ๊กยืนยันว่าท่านทราบว่าต้องมาเขียนคำร้องด้วยตนเองที่ห้องการเงินตามวันเวลานัด' });
                return;
            }
        }

        setSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            // แนบวัตถุประสงค์ไปกับเหตุผล เพื่อให้เจ้าหน้าที่เห็นว่าผู้ใช้จะนำเอกสารไปใช้ทำอะไร
            const reasonParts = [];
            if (purposeObj?.short && formData.request_type !== 'salary_cert') reasonParts.push(`วัตถุประสงค์: ${purposeObj.short}`);
            if ((formData.request_reason || '').trim()) reasonParts.push(formData.request_reason.trim());
            const payload = { ...formData, request_reason: reasonParts.join(' | ').slice(0, 500) };

            await api.post('/request', payload);
            setMessage({
                type: 'success',
                text: formData.request_type === 'salary_cert'
                    ? 'ส่งนัดหมายสำเร็จ — รอเจ้าหน้าที่ยืนยันนัด (ระบบจะแจ้งทางอีเมล) แล้วมาเขียนคำร้องตามวันเวลาที่นัดไว้'
                    : 'ส่งคำร้องสำเร็จ กรุณารอการอนุมัติ'
            });
            setShowForm(false);
            setCertAckInPerson(false);
            fetchRequests();
        } catch (err) {
            // ใช้ข้อความ message (ภาษาไทย) ก่อน error (machine code เช่น no_verified_email)
            setMessage({ type: 'error', text: err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSubmitting(false);
        }
    };

    const cancelRequest = async (id) => {
        if (!window.confirm('ยืนยันการยกเลิกคำร้อง?')) return;

        try {
            await api.delete(`/request/${id}`);
            setMessage({ type: 'success', text: 'ยกเลิกคำร้องสำเร็จ' });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    const handlePrint = async (request) => {
        if (!emailVerified) { window.location.href = '/profile#email'; return; }
        try {
            // Use signed endpoints for approved requests with self-print
            const endpoint = request.request_type === 'salary_slip'
                ? `/report/slip-signed/${request.id}`
                : `/report/50-tawi-signed/${request.id}`;

            const response = await api.get(endpoint, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Print error:', err);
            setMessage({ type: 'error', text: 'ไม่สามารถพิมพ์เอกสารได้' });
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: { label: 'รอดำเนินการ', class: 'badge-warning' },
            approved: { label: 'อนุมัติแล้ว', class: 'badge-success' },
            rejected: { label: 'ปฏิเสธ', class: 'badge-error' },
            completed: { label: 'เสร็จสิ้น', class: 'badge-info' },
            cancelled: { label: 'ยกเลิก', class: 'badge-muted' }
        };
        const info = statusMap[status] || { label: status, class: '' };
        return <span className={`badge ${info.class}`}>{info.label}</span>;
    };

    const getRequestTypeName = (type) => {
        const typeNames = {
            'salary_slip': 'สลิปเงินเดือน',
            'tax_cert': 'หนังสือรับรองภาษี (50 ทวิ)',
            'salary_cert': 'หนังสือรับรองเงินเดือน'
        };
        return typeNames[type] || type;
    };

    const getMonthName = (month) => {
        const months = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
            'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
        return months[month] || '';
    };

    // ข้อความนัดหมายเขียนคำร้อง (หนังสือรับรองเงินเดือน)
    const formatAppointment = (request) => {
        if (!request.appointment_date) return null;
        const d = new Date(request.appointment_date);
        if (isNaN(d.getTime())) return null;
        const dateText = d.toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' });
        return `${dateText} ${request.appointment_period === 'afternoon' ? 'ช่วงบ่าย (13:00–16:00 น.)' : 'ช่วงเช้า (08:30–12:00 น.)'}`;
    };

    // คำนวณเวลาที่ผ่านไปตั้งแต่ส่งคำร้อง
    const getTimeElapsed = (createdAt) => {
        const now = new Date();
        const created = new Date(createdAt);
        const diffMs = now - created;

        const minutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (days > 0) {
            const remainingHours = hours % 24;
            if (remainingHours > 0) {
                return `${days} วัน ${remainingHours} ชม.`;
            }
            return `${days} วัน`;
        }
        if (hours > 0) {
            const remainingMinutes = minutes % 60;
            if (remainingMinutes > 0) {
                return `${hours} ชม. ${remainingMinutes} นาที`;
            }
            return `${hours} ชม.`;
        }
        if (minutes > 0) {
            return `${minutes} นาที`;
        }
        return 'เมื่อสักครู่';
    };

    const currentYear = new Date().getFullYear();
    // 50 ทวิ ออกเป็น "รายปีภาษี" และออกได้เฉพาะปีที่จบแล้ว — ปีล่าสุดที่ขอได้คือปีที่แล้วเสมอ
    // (เคยมีคนขอ 50 ทวิ ของปีปัจจุบันซึ่งเป็นไปไม่ได้)
    const latestYearFor = (type) => (type === 'tax_cert' ? currentYear - 1 : currentYear);
    const years = [];
    for (let y = latestYearFor(formData.request_type); y >= currentYear - 5; y--) {
        years.push(y);
    }

    // มีเอกสารที่ "เปิดรับคำร้อง" อย่างน้อยหนึ่งประเภทหรือไม่ — ใช้ซ่อนปุ่ม/ฟอร์มยื่นคำร้องเมื่อปิดหมด
    const hasAnyRequestMode = requestModes.slip_request_mode || requestModes.tax_cert_request_mode || requestModes.salary_cert_request_mode;

    return (
        <div className="request-page">
            {/* Realtime Notification Toast */}
            {notification && (
                <div className={`notification-toast notification-${notification.type}`}>
                    <div className="notification-icon">
                        {notification.type === 'success' ? (
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                            </svg>
                        )}
                    </div>
                    <div className="notification-content">
                        <p>{notification.message}</p>
                    </div>
                    <button className="notification-close" onClick={() => setNotification(null)}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>
                </div>
            )}

            <div className="page-header">
                <div>
                    <h1>ยื่นคำร้อง</h1>
                    <p>ยื่นคำร้องขอเอกสาร — โปรดเลือกประเภทเอกสารให้ตรงกับวัตถุประสงค์การใช้งาน</p>
                </div>
                {hasAnyRequestMode && (
                    <button
                        className="steam-btn steam-btn-primary"
                        onClick={() => setShowForm(!showForm)}
                    >
                        {showForm ? 'ยกเลิก' : '+ ยื่นคำร้องใหม่'}
                    </button>
                )}
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* เปรียบเทียบเอกสาร 3 ประเภท — แก้ความเข้าใจผิดว่า 50 ทวิ / หนังสือรับรอง = สลิปเงินเดือน */}
            <div className="doc-compare-card mb-3">
                <div className="doc-compare-header">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                    </svg>
                    <div>
                        <h4>เอกสารแต่ละประเภทต่างกัน — เลือกให้ตรงกับที่จะนำไปใช้</h4>
                        <p>เอกสารทั้ง 3 ประเภทนี้<strong>ไม่ใช่เอกสารเดียวกัน</strong> และใช้แทนกันไม่ได้</p>
                    </div>
                </div>
                <div className="doc-compare-list">
                    {['salary_slip', 'tax_cert', 'salary_cert'].map(type => (
                        <div key={type} className={`doc-compare-item ${type}`}>
                            <div className="doc-compare-name">{DOC_INFO[type].name}</div>
                            <div className="doc-compare-use">ใช้สำหรับ: {DOC_INFO[type].use}</div>
                            <div className="doc-compare-desc">{DOC_INFO[type].desc}</div>
                        </div>
                    ))}
                </div>
                <div className="doc-compare-tip">
                    💡 <strong>จะกู้เงินกับสหกรณ์ออมทรัพย์หรือธนาคาร ใช้ "สลิปเงินเดือน"</strong> — ไม่ใช่ 50 ทวิ และส่วนใหญ่ไม่จำเป็นต้องใช้หนังสือรับรองเงินเดือน
                </div>
            </div>

            {/* Info message about what can be printed directly - Apple Style */}
            {(!requestModes.slip_request_mode || !requestModes.tax_cert_request_mode) && (
                <div className="apple-info-card mb-3">
                    <div className="apple-info-header">
                        <div className="apple-info-icon success">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                        </div>
                        <div className="apple-info-title">
                            <h4>เอกสารที่พิมพ์ได้เลย</h4>
                            <p>ไม่ต้องยื่นคำร้อง</p>
                        </div>
                    </div>

                    <div className="apple-info-body">
                        <div className="apple-doc-list">
                            {!requestModes.slip_request_mode && (
                                <div className="apple-doc-item available">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                    </svg>
                                    <span>สลิปเงินเดือน</span>
                                </div>
                            )}
                            {!requestModes.tax_cert_request_mode && (
                                <div className="apple-doc-item available">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                    </svg>
                                    <span>หนังสือรับรองภาษี (50 ทวิ)</span>
                                </div>
                            )}
                        </div>

                        <div className="apple-info-action">
                            <span>ท่านสามารถพิมพ์เอกสารข้างต้นได้โดยตรงจาก</span>
                            <button onClick={() => navigate('/')} className="apple-link-btn">
                                หน้าข้อมูลเงินเดือน
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* แสดงเอกสารที่ต้องยื่นคำร้อง */}
                    {(requestModes.slip_request_mode || requestModes.tax_cert_request_mode || requestModes.salary_cert_request_mode) && (
                        <div className="apple-info-footer">
                            <h5>เอกสารที่ต้องยื่นคำร้อง</h5>
                            <div className="apple-doc-list compact">
                                {requestModes.slip_request_mode && (
                                    <span className="apple-doc-tag">สลิปเงินเดือน</span>
                                )}
                                {requestModes.tax_cert_request_mode && (
                                    <span className="apple-doc-tag">หนังสือรับรองภาษี (50 ทวิ)</span>
                                )}
                                {requestModes.salary_cert_request_mode && (
                                    <span className="apple-doc-tag">หนังสือรับรองเงินเดือน</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* New Request Form */}
            {showForm && hasAnyRequestMode && (
                <div className="steam-card mb-3">
                    <div className="steam-card-header">ยื่นคำร้องใหม่</div>
                    <div className="steam-card-body">
                        <form onSubmit={handleSubmit}>
                            {/* ขั้นที่ 1: ระบุวัตถุประสงค์ก่อน — ระบบจะแนะนำเอกสารที่ถูกต้องให้ */}
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label className="form-label">จะนำเอกสารไปใช้ทำอะไร? <span className="required">*</span></label>
                                <select
                                    className="steam-input"
                                    value={purpose}
                                    onChange={(e) => handlePurposeChange(e.target.value)}
                                    required
                                >
                                    <option value="" disabled>— เลือกวัตถุประสงค์การใช้เอกสาร —</option>
                                    {PURPOSES.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                                <small className="form-hint">ระบบจะแนะนำเอกสารที่ถูกต้องตามวัตถุประสงค์ เพื่อไม่ให้ขอเอกสารผิดประเภท</small>
                            </div>

                            {/* คำแนะนำตามวัตถุประสงค์ */}
                            {recommendedType && (
                                <div className="doc-recommend-box" style={{ marginBottom: '16px' }}>
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                    </svg>
                                    <div>
                                        <strong>เอกสารที่เหมาะสมคือ: {DOC_INFO[recommendedType].name}</strong>
                                        <p>{DOC_INFO[recommendedType].desc}</p>
                                        {!recommendedModeOn && (recommendedType === 'salary_slip' || recommendedType === 'tax_cert') && (
                                            <div className="doc-recommend-direct">
                                                <p>
                                                    เอกสารนี้<strong>พิมพ์ได้ด้วยตนเองทันที ไม่ต้องยื่นคำร้อง</strong> จากหน้าข้อมูลเงินเดือน
                                                </p>
                                                <button type="button" onClick={() => navigate('/')} className="steam-btn steam-btn-primary" style={{ marginTop: 8 }}>
                                                    ไปหน้าข้อมูลเงินเดือน
                                                </button>
                                            </div>
                                        )}
                                        {!recommendedModeOn && recommendedType === 'salary_cert' && (
                                            <p>ขณะนี้ปิดรับคำร้องหนังสือรับรองเงินเดือนผ่านระบบ กรุณาติดต่อฝ่ายการเงินโดยตรง</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">ประเภทเอกสาร</label>
                                    <select
                                        className="steam-input"
                                        value={formData.request_type}
                                        onChange={(e) => {
                                            setMismatchConfirmed(false);
                                            setFormData({
                                                ...formData,
                                                request_type: e.target.value,
                                                // 50 ทวิ: รายปีเท่านั้น และปีล่าสุด = ปีที่แล้ว
                                                document_year: e.target.value === 'tax_cert'
                                                    ? Math.min(formData.document_year, currentYear - 1)
                                                    : formData.document_year,
                                                document_month: e.target.value === 'salary_slip' ? formData.document_month : null,
                                                request_reason: e.target.value === 'salary_cert' ? formData.request_reason : '',
                                                appointment_date: e.target.value === 'salary_cert' ? formData.appointment_date : '',
                                                appointment_period: e.target.value === 'salary_cert' ? formData.appointment_period : 'morning'
                                            });
                                        }}
                                    >
                                        {/* แสดงเฉพาะประเภทที่ต้องยื่นคำร้อง (mode เปิดอยู่) */}
                                        {requestModes.slip_request_mode && (
                                            <option value="salary_slip">สลิปเงินเดือน</option>
                                        )}
                                        {requestModes.tax_cert_request_mode && (
                                            <option value="tax_cert">หนังสือรับรองภาษี (50 ทวิ)</option>
                                        )}
                                        {requestModes.salary_cert_request_mode && (
                                            <option value="salary_cert">หนังสือรับรองเงินเดือน</option>
                                        )}
                                    </select>
                                    {/* แสดงข้อความถ้าบางประเภทพิมพ์ได้เลย */}
                                    {(!requestModes.slip_request_mode || !requestModes.tax_cert_request_mode) && (
                                        <small className="form-hint" style={{ color: '#86efac' }}>
                                            {!requestModes.slip_request_mode && !requestModes.tax_cert_request_mode
                                                ? 'สลิปและ 50 ทวิ สามารถพิมพ์ได้เลยจากหน้าข้อมูลเงินเดือน'
                                                : !requestModes.slip_request_mode
                                                    ? 'สลิปเงินเดือน สามารถพิมพ์ได้เลยจากหน้าข้อมูลเงินเดือน'
                                                    : '50 ทวิ สามารถพิมพ์ได้เลยจากหน้าข้อมูลเงินเดือน'
                                            }
                                        </small>
                                    )}
                                </div>

                                {/* หนังสือรับรองเงินเดือนไม่ผูกกับปี — รับรอง ณ ปัจจุบันเสมอ จึงไม่ต้องเลือกปี */}
                                {formData.request_type !== 'salary_cert' && (
                                    <div className="form-group">
                                        <label className="form-label">ปี พ.ศ.</label>
                                        <select
                                            className="steam-input"
                                            value={formData.document_year}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                document_year: parseInt(e.target.value)
                                            })}
                                        >
                                            {years.map(y => (
                                                <option key={y} value={y}>{y + 543}</option>
                                            ))}
                                        </select>
                                        {formData.request_type === 'tax_cert' && (
                                            <small className="form-hint" style={{ color: '#fbbf24' }}>
                                                50 ทวิ ออกเป็นรายปีภาษี — ขอได้เฉพาะปีที่จบแล้ว ล่าสุดคือปี {currentYear - 1 + 543}
                                                (ปี {currentYear + 543} ยังไม่จบปีภาษี จะขอได้ต้นปีหน้า)
                                            </small>
                                        )}
                                    </div>
                                )}

                                {formData.request_type === 'salary_slip' && (
                                    <div className="form-group">
                                        <label className="form-label">เดือน</label>
                                        <select
                                            className="steam-input"
                                            value={formData.document_month || ''}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                document_month: parseInt(e.target.value)
                                            })}
                                        >
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                                                <option key={m} value={m}>{getMonthName(m)}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* หนังสือรับรองเงินเดือน = ระบบนัดหมาย
                                คำร้องจริงต้องมาเขียนด้วยตนเองที่ห้องการเงิน — ที่กรอกตรงนี้คือนัดวันเวลา + เหตุผลคร่าว ๆ */}
                            {formData.request_type === 'salary_cert' && (
                                <>
                                    <div className="salary-cert-notice" style={{ marginTop: '16px' }}>
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                        </svg>
                                        <div>
                                            <strong>การยื่นนี้เป็นการนัดหมายล่วงหน้า</strong> — หนังสือรับรองเงินเดือนต้อง
                                            <b>มาเขียนคำร้องด้วยตนเองที่ห้องการเงิน</b> ตามวันเวลาที่นัดด้านล่าง (นำบัตรประชาชนมาด้วย)
                                            หลังเขียนคำร้องแล้ว การออกหนังสืออาจใช้เวลาหลายวันเพราะต้อง<b>เสนอผู้บริหารลงนาม</b>
                                            เมื่อหนังสือออกแล้วระบบจะส่งอีเมลแจ้งให้มารับ
                                        </div>
                                    </div>

                                    <div className="form-grid" style={{ marginTop: '12px' }}>
                                        <div className="form-group">
                                            <label className="form-label">วันที่จะเข้ามาเขียนคำร้อง <span className="required">*</span></label>
                                            <input
                                                type="date"
                                                className="steam-input"
                                                value={formData.appointment_date}
                                                min={new Date().toLocaleDateString('sv-SE')}
                                                onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                                                required
                                            />
                                            <small className="form-hint">วันทำการ จันทร์–ศุกร์ (เว้นวันหยุดราชการ)</small>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">ช่วงเวลา <span className="required">*</span></label>
                                            <select
                                                className="steam-input"
                                                value={formData.appointment_period}
                                                onChange={(e) => setFormData({ ...formData, appointment_period: e.target.value })}
                                            >
                                                <option value="morning">ช่วงเช้า (08:30–12:00 น.)</option>
                                                <option value="afternoon">ช่วงบ่าย (13:00–16:00 น.)</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginTop: '8px' }}>
                                        <label className="form-label">เหตุผลในการขอ (ใช้ทำอะไร ยื่นที่ไหน) <span className="required">*</span></label>
                                        <textarea
                                            className="steam-input"
                                            placeholder="ระบุคร่าว ๆ ว่านำไปใช้ทำอะไร กับหน่วยงานใด เช่น ใช้ประกอบการขอสินเชื่อบ้านกับธนาคารออมสิน, ใช้ยื่นวีซ่า, ใช้ค้ำประกัน ฯลฯ (รายละเอียดเต็มกรอกในคำร้องที่ห้องการเงิน)"
                                            value={formData.request_reason}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                request_reason: e.target.value
                                            })}
                                            rows={3}
                                            required
                                        />
                                        <small className="form-hint">ช่วยให้เจ้าหน้าที่เตรียมแบบคำร้องและข้อมูลไว้ล่วงหน้าก่อนท่านมาถึง</small>
                                    </div>

                                    <div className="form-group" style={{ marginTop: 12 }}>
                                        <label className="doc-mismatch-confirm">
                                            <input
                                                type="checkbox"
                                                checked={certAckInPerson}
                                                onChange={(e) => setCertAckInPerson(e.target.checked)}
                                            />
                                            <span>
                                                ข้าพเจ้าทราบว่าต้อง<b>มาเขียนคำร้องด้วยตนเองที่ห้องการเงิน</b>ตามวันเวลานัด
                                                และหนังสือจะออกหลังผู้บริหารลงนาม (ระบบจะแจ้งทางอีเมลเมื่อพร้อมรับ)
                                            </span>
                                        </label>
                                    </div>
                                </>
                            )}

                            {/* เตือนเมื่อเลือกเอกสารไม่ตรงกับวัตถุประสงค์ — ต้องติ๊กยืนยันก่อนส่ง */}
                            {typeMismatch && (
                                <div className="doc-mismatch-warning" style={{ marginTop: '16px' }}>
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                                    </svg>
                                    <div>
                                        <strong>เอกสารที่เลือกอาจไม่ตรงกับที่ท่านต้องการ</strong>
                                        <p>
                                            ตามวัตถุประสงค์ที่ระบุ เอกสารที่เหมาะสมคือ <strong>{DOC_INFO[recommendedType]?.name}</strong>{' '}
                                            ({DOC_INFO[recommendedType]?.use}) แต่ท่านเลือก <strong>{DOC_INFO[formData.request_type]?.name}</strong>{' '}
                                            ซึ่งใช้สำหรับ{DOC_INFO[formData.request_type]?.use} — เอกสารทั้งสองใช้แทนกันไม่ได้
                                        </p>
                                        <label className="doc-mismatch-confirm">
                                            <input
                                                type="checkbox"
                                                checked={mismatchConfirmed}
                                                onChange={(e) => setMismatchConfirmed(e.target.checked)}
                                            />
                                            <span>
                                                ข้าพเจ้าได้อ่านคำอธิบายความแตกต่างแล้ว และยืนยันว่าต้องการ
                                                <strong> {DOC_INFO[formData.request_type]?.name} </strong>จริง
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            <div className="form-group" style={{ marginTop: '16px' }}>
                                <label className="form-label">เบอร์โทรติดต่อกลับ <span className="required">*</span></label>
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    className="steam-input"
                                    placeholder="เช่น 081-234-5678"
                                    value={formData.contact_phone}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        contact_phone: e.target.value
                                    })}
                                    maxLength={15}
                                    required
                                />
                                <small className="form-hint">จำเป็นต้องกรอก เพื่อให้เจ้าหน้าที่ติดต่อกลับได้</small>
                            </div>

                            {/* แจ้งให้ยืนยันอีเมลก่อน สำหรับเอกสารที่ส่งผลทางอีเมล */}
                            {needsVerifiedEmail && !emailVerified && (
                                <div className="docreq-verify-banner" style={{ marginTop: '16px' }}>
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                                    </svg>
                                    <div>
                                        <strong>ต้องยืนยันอีเมลก่อน</strong>
                                        <p>เอกสารนี้จะแจ้งผลและจัดส่งทางอีเมล กรุณายืนยันอีเมลของคุณก่อนยื่นคำขอ</p>
                                        <Link to="/profile#email" className="steam-btn steam-btn-primary" style={{ marginTop: 8 }}>
                                            ไปยืนยันอีเมล
                                        </Link>
                                    </div>
                                </div>
                            )}

                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="steam-btn steam-btn-primary"
                                    disabled={submitting || (needsVerifiedEmail && !emailVerified) || (typeMismatch && !mismatchConfirmed)}
                                >
                                    {submitting ? 'กำลังส่ง...' : 'ส่งคำร้อง'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Request List */}
            <div className="steam-card">
                <div className="steam-card-header">ประวัติคำร้อง</div>
                <div className="steam-card-body">
                    {loading ? (
                        <div className="flex-center" style={{ padding: '40px 0' }}>
                            <div className="spinner"></div>
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="empty-state">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                            </svg>
                            <p>ยังไม่มีคำร้อง</p>
                            {hasAnyRequestMode ? (
                                <button
                                    className="steam-btn steam-btn-primary"
                                    onClick={() => setShowForm(true)}
                                >
                                    ยื่นคำร้องแรกของคุณ
                                </button>
                            ) : (
                                <p className="text-muted" style={{ marginTop: 8 }}>
                                    ขณะนี้ไม่มีเอกสารที่ต้องยื่นคำร้อง — สลิปเงินเดือนและ 50 ทวิ สามารถพิมพ์ได้เลยจากหน้าข้อมูลเงินเดือน
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="request-list">
                            {requests.map(request => (
                                <div key={request.id} className="request-item">
                                    <div className="request-info">
                                        <div className="request-type">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                                            </svg>
                                            <div>
                                                <h4>{getRequestTypeName(request.request_type)}</h4>
                                                {/* หนังสือรับรองเงินเดือนไม่ผูกกับปี — แสดงนัดหมายเขียนคำร้องแทนงวด */}
                                                {request.request_type !== 'salary_cert' && (
                                                    <p>
                                                        {request.request_type === 'salary_slip'
                                                            ? `${getMonthName(request.document_month)} ${request.document_year + 543}`
                                                            : `ปี ${request.document_year + 543}`
                                                        }
                                                    </p>
                                                )}
                                                {request.request_type === 'salary_cert' && formatAppointment(request) && (
                                                    <p>
                                                        📅 นัดเขียนคำร้อง: {formatAppointment(request)}
                                                        {request.status === 'pending' && <small> (รอเจ้าหน้าที่ยืนยันนัด)</small>}
                                                        {request.status === 'approved' && <small> — ยืนยันนัดแล้ว มาเขียนคำร้องที่ห้องการเงินตามนัด</small>}
                                                        {request.status === 'completed' && <small> — หนังสือออกแล้ว มารับได้ที่ห้องการเงิน</small>}
                                                    </p>
                                                )}
                                                {request.request_reason && (
                                                    <p className="request-reason-text">
                                                        <small>เหตุผล: {request.request_reason}</small>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="request-meta">
                                            {getStatusBadge(request.status)}
                                            <span className="request-date">
                                                {new Date(request.created_at).toLocaleDateString('th-TH', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false
                                                })}
                                            </span>
                                            {request.status === 'pending' && (
                                                <span className="time-elapsed pending">
                                                    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                                                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                                    </svg>
                                                    รอ {getTimeElapsed(request.created_at)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Status Details */}
                                    {request.status === 'approved' && (
                                        <div className="request-detail approved">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                            </svg>
                                            <div>
                                                {request.allow_self_print ? (
                                                    <p>อนุมัติให้พิมพ์เอกสารได้เอง</p>
                                                ) : (
                                                    <p>รับเอกสารที่: {request.pickup_location || 'ฝ่ายการเงิน'}</p>
                                                )}
                                                <p className="approver-info">
                                                    อนุมัติโดย: {request.approved_by_name || 'ไม่ระบุ'} |
                                                    เมื่อ {request.approved_at ? new Date(request.approved_at).toLocaleDateString('th-TH', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: false
                                                    }) : '-'}
                                                </p>
                                                {request.admin_note && (
                                                    <p className="admin-note">หมายเหตุ: {request.admin_note}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {request.status === 'pending' && (
                                        <div className="request-detail pending-info">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                            </svg>
                                            <div>
                                                <p><strong>ฝ่ายการเงินได้รับคำร้องแล้ว</strong></p>
                                                <p>อยู่ระหว่างตรวจสอบข้อมูล กรุณาเข้ามาตรวจสอบสถานะอีกครั้งในภายหลัง</p>
                                            </div>
                                        </div>
                                    )}

                                    {request.status === 'rejected' && (
                                        <div className="request-detail rejected">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                            </svg>
                                            <div>
                                                <p>เหตุผล: {request.rejected_reason}</p>
                                                <p className="approver-info">
                                                    โดย: {request.approved_by_name || 'ไม่ระบุ'} |
                                                    เมื่อ {request.approved_at ? new Date(request.approved_at).toLocaleDateString('th-TH', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: false
                                                    }) : '-'}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="request-actions">
                                        {request.status === 'pending' && (
                                            <button
                                                className="steam-btn steam-btn-secondary"
                                                onClick={() => cancelRequest(request.id)}
                                            >
                                                ยกเลิกคำร้อง
                                            </button>
                                        )}
                                        {request.status === 'approved' && request.allow_self_print && (
                                            <button
                                                className="steam-btn steam-btn-primary"
                                                onClick={() => handlePrint(request)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                    <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                                                </svg>
                                                พิมพ์เอกสาร
                                            </button>
                                        )}
                                        {/* ติดตามคำร้อง — เฉพาะคำร้องที่ยังไม่จบ (รอ/อนุมัติแต่ยังไม่ได้รับเอกสาร) */}
                                        {(request.status === 'pending' || (request.status === 'approved' && !request.allow_self_print)) && (
                                            <button
                                                className="steam-btn steam-btn-secondary"
                                                onClick={() => openFollowUp(request.id)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                                </svg>
                                                ติดตาม / ส่งข้อความถึงเจ้าหน้าที่
                                            </button>
                                        )}
                                    </div>

                                    {/* ฟอร์มส่งข้อความติดตาม */}
                                    {followUpFor === request.id && (
                                        <div className="followup-box">
                                            {!emailVerified ? (
                                                <div className="followup-verify">
                                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                                                    </svg>
                                                    <div>
                                                        <p><strong>กรุณายืนยันอีเมลก่อน</strong></p>
                                                        <p className="followup-verify-desc">
                                                            หากยังไม่ยืนยันอีเมล จะเป็นการส่งข้อความฝ่ายเดียว เจ้าหน้าที่จะไม่สามารถติดต่อกลับได้
                                                            กรุณายืนยันอีเมลให้เรียบร้อยก่อน
                                                        </p>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <Link to="/profile#email" className="steam-btn steam-btn-primary">ไปยืนยันอีเมล</Link>
                                                            <button className="steam-btn steam-btn-secondary" onClick={() => setFollowUpFor(null)}>ปิด</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="followup-note">
                                                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                                                        </svg>
                                                        <span>
                                                            หากยังไม่ได้รับเอกสาร ไม่ได้รับการติดต่อกลับ หรือสถานะไม่เปลี่ยน
                                                            ส่งข้อความถึงเจ้าหน้าที่ได้ที่นี่ — ข้อความจะถูกส่งเข้ากลุ่มเจ้าหน้าที่การเงิน
                                                            พร้อมข้อมูลคำร้องของคุณ <strong>โปรดใช้ถ้อยคำสุภาพ</strong>
                                                        </span>
                                                    </div>
                                                    <textarea
                                                        className="followup-textarea"
                                                        value={followUpText}
                                                        onChange={(e) => setFollowUpText(e.target.value.slice(0, 500))}
                                                        placeholder="เช่น ขอสอบถามความคืบหน้าของคำร้องนี้ครับ/ค่ะ ขอบคุณครับ/ค่ะ"
                                                        rows="3"
                                                        maxLength="500"
                                                    />
                                                    <div className="followup-actions">
                                                        <span className="followup-count">{followUpText.length}/500</span>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <button className="steam-btn steam-btn-secondary" onClick={() => setFollowUpFor(null)} disabled={followUpSending}>
                                                                ยกเลิก
                                                            </button>
                                                            <button className="steam-btn steam-btn-primary" onClick={() => sendFollowUp(request.id)} disabled={followUpSending}>
                                                                {followUpSending ? 'กำลังส่ง...' : 'ส่งข้อความ'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DocumentRequest;
